import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import { QirtaasEditor, type QirtaasEditorHandle } from '@qirtaas/react'
import { createDocument, updateDocumentContent } from './qirtaas'


type JsonNode={type?:string;attrs?:Record<string,unknown>;text?:string;marks?:unknown[];content?:JsonNode[];[key:string]:unknown}

function nodeText(node:JsonNode):string{return typeof node.text==='string'?node.text:(node.content||[]).map(nodeText).join('')}
function mixedDirection(text:string):'ltr'|'rtl'|null{
  if(!text)return null
  const arabic=(text.match(/[\u0591-\u07ff\ufb1d-\ufdfd\ufe70-\ufefc]/g)||[]).length
  const latin=(text.match(/[A-Za-z\u00c0-\u02b8]/g)||[]).length
  if(!arabic)return latin?'ltr':null
  if(!latin)return'rtl'
  return arabic/(arabic+latin)>=.6?'rtl':'ltr'
}
function stripInlinePrefix(content:JsonNode[]|undefined,count:number){
  let left=count
  const out:JsonNode[]=[]
  for(const child of content||[]){
    if(left>0&&typeof child.text==='string'){
      if(child.text.length<=left){left-=child.text.length;continue}
      out.push({...child,text:child.text.slice(left)});left=0;continue
    }
    out.push(child)
  }
  return out
}
function splitBulletTasksRaw(node:JsonNode):JsonNode[]{
  if(node.type!=='bulletList'||!Array.isArray(node.content)||!node.content.length)return[{...node,content:Array.isArray(node.content)?node.content.map(migrateNode):node.content}]
  const groups:Array<{task:boolean;items:JsonNode[]}>=[] 
  for(const item of node.content){
    let task=false,nextItem:JsonNode
    if(item.type==='listItem'&&Array.isArray(item.content)&&item.content.length&&item.content.every(child=>child.type==='paragraph')){
      const first=item.content[0],text=nodeText(first),match=text.match(/^\s*\[([ xX])\]\s*/)
      if(match){
        task=true
        const paragraphs=item.content.map((child,index)=>migrateNode(index===0?{...child,content:stripInlinePrefix(child.content,match[0].length)}:child))
        nextItem={type:'taskItem',attrs:{checked:match[1].toLowerCase()==='x'},content:paragraphs}
      }else nextItem=migrateNode(item)
    }else nextItem=migrateNode(item)
    const last=groups[groups.length-1]
    if(last&&last.task===task)last.items.push(nextItem)
    else groups.push({task,items:[nextItem]})
  }
  return groups.map(group=>group.task?{type:'taskList',attrs:{},content:group.items}:{...node,content:group.items})
}
function migrateNode(node:JsonNode):JsonNode{
  if(node.type==='bulletList'){
    const split=splitBulletTasksRaw(node)
    if(split.length===1)return split[0]
  }
  const content=Array.isArray(node.content)?node.content.flatMap(child=>child.type==='bulletList'?splitBulletTasksRaw(child):[migrateNode(child)]):node.content
  if(node.type==='paragraph'||node.type==='heading'){
    const dir=mixedDirection(nodeText({...node,content}))
    const attrs={...(node.attrs||{})}
    if(dir)attrs.dir=dir;else delete attrs.dir
    return{...node,attrs,content}
  }
  if(node.type==='listItem'||node.type==='bulletItem'){
    const attrs={...(node.attrs||{})};delete attrs.dir
    return{...node,attrs,content}
  }
  return content?{...node,content}:node
}

type Props = {
  documentId?: string
  revision: number
  getToken: () => Promise<string>
  onDocumentCreated: (id: string) => void
  onChange: (json: Record<string, unknown>) => void
  onSaved?: (json: Record<string, unknown>) => void
}

/**
 * Qirtaas performs asynchronous document loading inside its editor island.
 * Rapid prop changes can start overlapping loads, so only commit the latest
 * requested document after a short quiet period. While a switch is pending,
 * hide the previous editor so the UI never shows content from the wrong note.
 * Qirtaas can also emit several intermediate onChange snapshots while hydrating;
 * debounce those so Dawwin metadata sees the settled snapshot, not a partial one.
 *
 * Unsaved edits: the Qirtaas SDK discards edits still waiting in its autosave debounce
 * when an editor unmounts. Before switching documents (or remounting after an external
 * write) the previous editor therefore stays mounted, hidden, until its pending edits are
 * flushed. If that flush fails, the previous note stays on screen with an explicit
 * Retry / Discard choice instead of silently losing the edits.
 */
/** An insertion/import was refused to protect the document; message is user-facing. */
export class InsertBlockedError extends Error {}
export type ExternalWrite={base:Record<string,unknown>;release:()=>void}
export type StableQirtaasEditorHandle={
  openCommandPalette:()=>void;openShortcuts:()=>void;save:()=>Promise<void>
  /**
   * Prepares a whole-document write (Qurʾān/hadith insertion, Markdown import) for the
   * document currently open in this editor: refuses if the document is not loaded (or failed
   * to load), flushes pending edits, captures the editor's content as the base to modify, and
   * then UNMOUNTS the editor so it cannot autosave stale content over the write. After a
   * successful (or unconfirmed) write, bump `revision` to remount from Qirtaas; if the write is
   * not performed or was rejected, call release() to remount the unchanged document.
   *
   * (The SDK's setEditable() must not be used for this: toggling it emits an editor update,
   * which the SDK treats as an edit and autosaves -- that overwrote insertions and crashed the
   * Vue island when the editor was then torn down mid-update.)
   */
  prepareExternalWrite:(documentId:string)=>Promise<ExternalWrite>
}

function isDocJson(value:unknown):value is Record<string,unknown>{
  return !!value&&typeof value==='object'&&(value as {type?:unknown}).type==='doc'&&Array.isArray((value as {content?:unknown}).content)
}

type SaveIndicatorState='saved'|'saving'|'error'
const FLUSH_TIMEOUT_MS=15000

/**
 * Saves of editors that have already been unmounted (the user left the writing desk for a Study
 * Space, Today, Calendar... with edits still pending or in flight). An entry stays here -- shown in
 * the header indicator, retryable, guarded on reload -- until Qirtaas CONFIRMS the save. Starting a
 * save is never treated as success. Keyed by document id ('new:<source>' for an unnamed note).
 *
 * The first attempt is the SDK's own save, started before the editor is destroyed. Once destroyed,
 * the SDK handle's save() silently does nothing, so retries write the content snapshot taken at
 * unmount through Dawwin's API, whose response confirms the write.
 */
type DetachedSave={key:string;source:string;documentId:string|null;snapshot:Record<string,unknown>|null;handle:QirtaasEditorHandle|null;report:(state:SaveIndicatorState|'detached')=>void;attempt:Promise<boolean>;failed:boolean}
const detachedSaves=new Map<string,DetachedSave>()
async function writeDetached(entry:DetachedSave):Promise<void>{
  const handle=entry.handle
  if(handle){entry.handle=null;await handle.save();return}
  if(!isDocJson(entry.snapshot))throw new Error('No content snapshot to retry')
  entry.report('saving')
  if(entry.documentId)await updateDocumentContent(entry.documentId,entry.snapshot)
  else entry.documentId=(await createDocument(entry.snapshot)).id
  entry.report('saved')
}
function attemptDetachedSave(entry:DetachedSave):Promise<boolean>{
  entry.failed=false
  entry.attempt=writeDetached(entry).then(()=>{
    if(detachedSaves.get(entry.key)===entry){detachedSaves.delete(entry.key);entry.report('detached')}
    return true
  },error=>{
    console.warn('Changes to a note you left could not be saved; they are kept for Retry',error)
    entry.failed=true;entry.report('error')
    return false
  })
  return entry.attempt
}
/** Wait (bounded) until a left-behind save of this document is confirmed. False = not confirmed. */
async function settleDetachedSave(documentId:string):Promise<boolean>{
  const entry=detachedSaves.get(documentId)
  if(!entry)return true
  const attempt=entry.failed?attemptDetachedSave(entry):entry.attempt
  let timer:number|undefined
  try{return await Promise.race([attempt,new Promise<boolean>(resolve=>{timer=window.setTimeout(()=>resolve(false),FLUSH_TIMEOUT_MS)})])}
  finally{if(timer!==undefined)window.clearTimeout(timer)}
}
if(typeof window!=='undefined'){
  window.addEventListener('dawwin:save-retry',()=>{for(const entry of detachedSaves.values())if(entry.failed)void attemptDetachedSave(entry)})
  window.addEventListener('beforeunload',event=>{if(detachedSaves.size){event.preventDefault();event.returnValue=''}})
}

const StableQirtaasEditor=forwardRef<StableQirtaasEditorHandle,Props>(function StableQirtaasEditor({ documentId, revision, getToken, onDocumentCreated, onChange, onSaved },ref){
  const wantedKey = `${documentId || 'new'}:${revision}`
  const [mountedKey, setMountedKey] = useState(documentId?'':wantedKey)
  const [theme,setTheme]=useState<'dark'|'light'>(()=>document.documentElement.dataset.theme==='light'?'light':'dark')
  const [switching, setSwitching] = useState(false)
  const [flushBlocked, setFlushBlocked] = useState(false)
  const [flushAttempt, setFlushAttempt] = useState(0)
  const [suspendedForWrite, setSuspendedForWrite] = useState(false)
  const editorReady = mountedKey === wantedKey && !switching && !suspendedForWrite
  const latestWanted = useRef(wantedKey)
  const changeTimer = useRef<number | undefined>(undefined)
  const editorHost = useRef<HTMLDivElement | null>(null)
  const qirtaasRef=useRef<QirtaasEditorHandle|null>(null)
  const saveSource=useRef(`editor-${Math.random().toString(36).slice(2)}`)
  const lastSaveState=useRef<SaveIndicatorState>('saved')
  const loadFailedKey=useRef('')
  const liveState=useRef({mountedKey,editorReady})
  liveState.current={mountedKey,editorReady}
  useImperativeHandle(ref,()=>({
    openCommandPalette:()=>qirtaasRef.current?.openCommandPalette(),
    openShortcuts:()=>qirtaasRef.current?.openShortcuts(),
    save:()=>qirtaasRef.current?.save()??Promise.resolve(),
    prepareExternalWrite:async(targetId:string)=>{
      const {mountedKey:key,editorReady:ready}=liveState.current
      const handle=qirtaasRef.current
      if(!ready||!handle||key.split(':')[0]!==targetId)throw new InsertBlockedError('This note is still opening. Nothing was inserted; please try again in a moment.')
      if(loadFailedKey.current===key)throw new InsertBlockedError('This note could not be loaded from Qirtaas, so nothing was inserted (to avoid overwriting it). Reopen the note and try again.')
      if(!(await flushMounted()))throw new InsertBlockedError('Your latest changes to this note have not been saved yet, so nothing was inserted. Use Retry in the save indicator, then insert again.')
      if(liveState.current.mountedKey!==key||qirtaasRef.current!==handle)throw new InsertBlockedError('This note changed while preparing the insertion. Nothing was inserted; please try again.')
      const json=handle.getJSON()
      if(!isDocJson(json))throw new InsertBlockedError('This note’s content is not available right now, so nothing was inserted (to avoid overwriting it).')
      const base=JSON.parse(JSON.stringify(json)) as Record<string,unknown>
      // Captured after the flush and in the same tick as the unmount, so `base` includes every
      // edit the user made; the unmounted editor can no longer autosave over the write.
      setSuspendedForWrite(true)
      return{base,release:()=>setSuspendedForWrite(false)}
    },
  }),[])

  function reportSaveState(state:SaveIndicatorState|'detached'){
    if(state!=='detached')lastSaveState.current=state
    window.dispatchEvent(new CustomEvent('dawwin:save-state',{detail:{state,source:saveSource.current}}))
  }
  // Leaving the writing desk (Study Space, Today, Calendar...) unmounts this whole component,
  // and the SDK drops edits still waiting in its autosave debounce when it is destroyed. A layout
  // cleanup runs before the SDK's own (passive) destroy, so start the pending save here and track
  // it until Qirtaas confirms it (see detachedSaves). Unchanged notes resolve without a write.
  useLayoutEffect(()=>()=>{
    const handle=qirtaasRef.current
    if(!handle)return
    const [id]=liveState.current.mountedKey.split(':')
    const key=id&&id!=='new'?id:`new:${saveSource.current}`
    const snapshot=handle.getJSON()
    const entry:DetachedSave={key,source:saveSource.current,documentId:id&&id!=='new'?id:null,snapshot:isDocJson(snapshot)?JSON.parse(JSON.stringify(snapshot)):null,handle,report:reportSaveState,attempt:Promise.resolve(true),failed:false}
    detachedSaves.set(key,entry)
    void attemptDetachedSave(entry)
  },[])
  // Keep this editor's indicator entry while its left-behind save is unconfirmed.
  useEffect(()=>()=>{if(![...detachedSaves.values()].some(entry=>entry.source===saveSource.current))reportSaveState('detached')},[])
  // A reload or closed tab while a save is unconfirmed would lose it: the SDK only warns while
  // its debounce is pending, not while the save request is in flight or after it failed.
  useEffect(()=>{
    const warn=(event:BeforeUnloadEvent)=>{if(lastSaveState.current!=='saved'){event.preventDefault();event.returnValue=''}}
    window.addEventListener('beforeunload',warn)
    return()=>window.removeEventListener('beforeunload',warn)
  },[])
  // "Retry" in the header save indicator: re-attempt only an editor whose last save failed.
  useEffect(()=>{
    const retry=()=>{if(lastSaveState.current==='error')void qirtaasRef.current?.save().catch(()=>{})}
    window.addEventListener('dawwin:save-retry',retry)
    return()=>window.removeEventListener('dawwin:save-retry',retry)
  },[])

  /** Persist edits still pending in the mounted editor. Resolves false if they could not be saved. */
  async function flushMounted():Promise<boolean>{
    const handle=qirtaasRef.current
    if(!handle)return true
    let timer:number|undefined
    try{
      await Promise.race([handle.save(),new Promise<never>((_,reject)=>{timer=window.setTimeout(()=>reject(new Error('flush_timeout')),FLUSH_TIMEOUT_MS)})])
      return true
    }catch(error){
      console.warn('Pending editor changes could not be saved before switching',error)
      return false
    }finally{
      if(timer!==undefined)window.clearTimeout(timer)
    }
  }

  useEffect(()=>{const sync=()=>setTheme(document.documentElement.dataset.theme==='light'?'light':'dark');window.addEventListener('dawwin:theme-change',sync);return()=>window.removeEventListener('dawwin:theme-change',sync)},[])

  useEffect(() => {
    const host = editorHost.current
    if (!host) return

    const applyInsertForeground = () => {
      const foreground = theme === 'light' ? '#11110f' : '#f1f0eb'
      host.querySelectorAll('button').forEach(button => {
        const className = typeof button.className === 'string' ? button.className : ''
        if (!className.includes('!text-white') || button.textContent?.trim() !== 'Insert') return
        button.style.setProperty('color', foreground, 'important')
        button.querySelectorAll<HTMLElement>('*').forEach(child => {
          child.style.setProperty('color', foreground, 'important')
          child.style.setProperty('fill', 'currentColor', 'important')
          child.style.setProperty('stroke', 'currentColor', 'important')
        })
      })
    }

    applyInsertForeground()
    const observer = new MutationObserver(applyInsertForeground)
    observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [theme, mountedKey])


  useEffect(() => {
    latestWanted.current = wantedKey
    if (wantedKey === mountedKey) { setSwitching(false); setFlushBlocked(false); return }
    setSwitching(true)
    let cancelled=false
    // Let Qirtaas load the requested document directly. The former migration
    // preflight performed an extra authenticated GET and could PATCH content
    // before the editor loaded, compounding upstream failures.
    const timer=window.setTimeout(()=>{
      void (async()=>{
        if(cancelled||latestWanted.current!==wantedKey)return
        // Reopening a note whose save was left in flight (or failed) after leaving it: loading now
        // would show the older stored copy and the next autosave would overwrite the late save.
        const flushed=await flushMounted()&&(!documentId||await settleDetachedSave(documentId))
        if(cancelled||latestWanted.current!==wantedKey)return
        if(flushed){setFlushBlocked(false);setSuspendedForWrite(false);setMountedKey(wantedKey)}
        else setFlushBlocked(true)
      })()
    },documentId?120:0)
    return()=>{cancelled=true;window.clearTimeout(timer)}
  }, [wantedKey, mountedKey, documentId, flushAttempt])

  useEffect(() => {
    window.clearTimeout(changeTimer.current)
    if (mountedKey === wantedKey) setSwitching(false)
    return () => window.clearTimeout(changeTimer.current)
  }, [mountedKey, wantedKey])

  const [mountedDocumentId] = mountedKey.split(':')
  const editorDocumentId = mountedDocumentId === 'new' ? undefined : mountedDocumentId

  return (
    <>
      {!editorReady && !flushBlocked && <div className="editor-switching" aria-label="Loading document" />}
      {flushBlocked && <div className="editor-flush-blocked" role="alert">
        <span>Your latest changes to this note have not been saved: Qirtaas did not confirm them. They are still shown below.</span>
        <button type="button" onClick={()=>{setFlushBlocked(false);setFlushAttempt(n=>n+1)}}>Retry saving</button>
        <button type="button" onClick={()=>{if(window.confirm('Discard the unsaved changes to this note and continue?')){const [target]=latestWanted.current.split(':');const left=detachedSaves.get(target);if(left){detachedSaves.delete(target);left.report('detached')}setFlushBlocked(false);setMountedKey(latestWanted.current)}}}>Discard unsaved changes</button>
      </div>}
      <div ref={editorHost} style={{ display: editorReady || flushBlocked ? 'contents' : 'none' }}>
        {mountedKey&&!suspendedForWrite&&<QirtaasEditor
          ref={qirtaasRef}
          key={mountedKey}
          getToken={getToken}
          documentId={editorDocumentId}
          locale="en"
          theme={theme}
          onDocumentCreated={onDocumentCreated}
          onError={code => { if (code === 'load_failed') loadFailedKey.current = mountedKey }}
          onSaveStateChange={state => {
            // Report real save outcomes even for a hidden editor that is flushing before a switch.
            if (state === 'saving') reportSaveState('saving')
            else if (state === 'error') reportSaveState('error')
            else if (state === 'saved') {
              reportSaveState('saved')
              if (mountedKey !== latestWanted.current) return
              const json=qirtaasRef.current?.getJSON()
              if(json) onSaved?.(json as Record<string, unknown>)
            }
          }}
          onChange={json => {
            if (mountedKey !== latestWanted.current) return
            // Pending edits of an existing document: show "Saving…" until Qirtaas confirms.
            if (editorDocumentId && lastSaveState.current !== 'saving') reportSaveState('saving')
            window.clearTimeout(changeTimer.current)
            changeTimer.current = window.setTimeout(() => {
              if (mountedKey === latestWanted.current) onChange(json)
            }, 350)
          }}
        />}
      </div>
    </>
  )
})

export default StableQirtaasEditor
